// The per-tab info-page articles (AI-637), keyed by V3 tab id. Every page has the same shape;
// a tab with no entry (Profil) gets no info icon — Core.showInfoButton.

// Bold lead (rendered with a colon) + text.
export interface V3InfoPageLeadText {
  lead: string;
  text: string;
}

export interface V3InfoPagePicture {
  src: string;
  width: number;
  height: number;
}

// The PNGs carry transparent padding, so they render 15% larger than their Figma frame.
const PICTURE_SCALE = 1.15;

function picture(src: string, figmaWidth: number, figmaHeight: number): V3InfoPagePicture {
  const scale = (px: number) => Math.round(px * PICTURE_SCALE * 10) / 10;
  return { src, width: scale(figmaWidth), height: scale(figmaHeight) };
}

export interface V3InfoPageContent {
  image: V3InfoPagePicture;
  title: string;
  intro: string;
  whyTitle: string;
  why: string;
  showsTitle: string;
  // One per part of the analysis.
  shows: V3InfoPageLeadText[];
  // One line under the "shows" list.
  conclusion?: string;
  usageTitle: string;
  usage: string[];
  // One line under the "usage" list, with bold anywhere (a lead, a closing sentence). Runs
  // are concatenated as-is, so the separating space belongs inside a run.
  closingNote?: V3InfoPageRun[];
}

export interface V3InfoPageRun {
  text: string;
  bold?: boolean;
}

export const V3_INFO_PAGE_DISCLAIMER =
  'Die finanzen AI Analyse dient der Information und unterstützt dich bei deiner eigenständigen Beschäftigung mit Wertpapieren. Sie stellt keine individuelle Anlageberatung und keine persönliche Anlageempfehlung dar.';

export function hasV3InfoPage(tab: string): boolean {
  return Object.hasOwn(V3_INFO_PAGE_CONTENT, tab);
}

export const V3_INFO_PAGE_CONTENT: Record<string, V3InfoPageContent> = {
  news: {
    image: picture('/v3/info/news.png', 126.6, 107.2),
    title: 'Aus der Nachrichtenflut das, was den Kurs bewegt.',
    intro:
      'Zu jeder Aktie erscheinen täglich Dutzende Meldungen, verteilt über Agenturen, Unternehmensmitteilungen und Portale. Die News-Analyse bündelt sie an einem Ort, filtert das Wesentliche heraus und ordnet ein, welche Ereignisse den Kurs kurz- und langfristig beeinflussen können.',
    whyTitle: 'Warum sich dieser Blick lohnt',
    why: 'Künstliche Intelligenz verknüpft jede Meldung mit dem, was sie für das Unternehmen bedeutet. Statt Schlagzeilen einzeln zu bewerten, siehst du in Sekunden, welche Themen die Aktie gerade tragen – und welche als Nächstes kommen.',
    showsTitle: 'Was die News-Analyse zeigt:',
    shows: [
      {
        lead: 'Highlight',
        text: 'Die Meldung mit dem aktuell größten Gewicht, zusammengefasst in wenigen Sätzen.',
      },
      {
        lead: 'Top-Headlines',
        text: 'Die wichtigsten Nachrichten der letzten Tage, jeweils mit Datum und Kurzfassung.',
      },
      {
        lead: 'Kurzfristige Katalysatoren',
        text: 'Ereignisse der kommenden Wochen, die Bewegung auslösen können – etwa Quartalszahlen, Produktvorstellungen oder Gerichtsentscheidungen.',
      },
      {
        lead: 'Langfristige Katalysatoren',
        text: 'Strukturelle Themen, die das Geschäft über Monate und Jahre prägen – etwa neue Märkte, Regulierung oder technologische Verschiebungen.',
      },
    ],
    conclusion:
      'Ein Katalysator sagt nichts über die Richtung. Dasselbe Ereignis kann den Kurs stützen oder belasten, je nachdem, wie es ausfällt.',
    usageTitle: 'So nutzt du die News-Analyse',
    usage: [
      'Mit dem Highlight starten. In unter einer Minute weißt du, worüber gerade gesprochen wird.',
      'Zeithorizont abgleichen. Kurzfristige Katalysatoren erklären oft Schwankungen, langfristige sagen mehr über die Substanz. Gewichte sie danach, wie lange du investiert sein willst.',
      'Bei wichtigen Meldungen tiefer gehen. Verändert eine Nachricht dein Bild, lies die vollständige Meldung. Die Zusammenfassung ist der Einstieg, nicht das Ende.',
      'News mit Zahlen verbinden. Prüfe in Earnings und Analysten, ob sich eine Meldung schon in Ergebnissen oder Einschätzungen niederschlägt.',
    ],
  },
  earnings: {
    image: picture('/v3/info/earnings.png', 131.1, 121.9),
    title: 'Quartalszahlen mit dem Blick institutioneller Investoren.',
    intro:
      'Quartalszahlen sind der Moment, in dem Erwartungen auf Realität treffen – und der Moment, in dem die meisten Anleger nur die Schlagzeile lesen. Die Earnings-Analyse zeigt dir, wie das Ergebnis ausgefallen ist, was dahintersteckt und worauf es beim nächsten Bericht ankommt.',
    whyTitle: 'Die Datenbasis dahinter',
    why: 'Die Kennzahlen stammen von FactSet, einem Datenanbieter, mit dem institutionelle Investoren weltweit arbeiten. Berichte, Präsentationen und die Inhalte der Earnings Calls liefert Quartr. Was du sonst über Terminals oder Abonnements zusammensuchst und in einem oft einstündigen Call nachhörst, bereitet die AI Analyse in wenigen Minuten Lesezeit für dich auf – inklusive, ohne Aufpreis.',
    showsTitle: 'Was die Earnings-Analyse zeigt',
    shows: [
      {
        lead: 'EPS vs. Erwartung',
        text: 'Der Gewinn je Aktie im Vergleich zur Schätzung der Analysten, mit Abweichung in Prozent. Der Chart zeigt, wie oft das Unternehmen die Erwartungen in den letzten Quartalen übertroffen oder verfehlt hat, dazu den Termin des nächsten Berichts.',
      },
      {
        lead: 'Gewinn vs. Umsatz',
        text: 'Die Entwicklung beider Größen im Jahresvergleich. So siehst du, ob Wachstum auch beim Gewinn ankommt.',
      },
      {
        lead: 'Updates aus den Earnings',
        text: 'Die positiven und negativen Punkte aus dem Bericht, getrennt aufbereitet.',
      },
      {
        lead: 'FAQ zum Earnings Call',
        text: 'Die zentralen Fragen der Analysten an das Management und die Antworten darauf.',
      },
      {
        lead: 'Ausblick',
        text: 'Die Prognose des Managements und die Themen, auf die der Markt beim nächsten Bericht achtet.',
      },
    ],
    usageTitle: 'So nutzt du die Earnings-Analyse',
    usage: [
      'Die Abweichung im Verlauf sehen. Ein Beat ist nur so viel wert wie die Erwartung davor. Übertrifft ein Unternehmen regelmäßig, rechnet der Markt oft schon damit.',
      'Umsatz und Gewinn zusammen lesen. Wächst der Gewinn schneller als der Umsatz, verbessert sich meist die Marge. Wächst nur der Umsatz, lohnt der Blick auf die Kosten.',
      'Mit den negativen Updates beginnen. Die positiven stehen meist in der Schlagzeile. Die negativen zeigen, wo die Risiken liegen.',
      'Im FAQ auf die Tonlage achten. Wo das Management konkret wird und wo es vage bleibt, verrät oft mehr als die Zahl selbst.',
      'Den Ausblick als Messlatte nutzen. Beim nächsten Bericht siehst du, ob das Unternehmen geliefert hat, was es angekündigt hat.',
    ],
    closingNote: [
      { text: 'Übrigens:', bold: true },
      {
        text: ' Auch starke Zahlen können den Kurs belasten, wenn der Ausblick hinter den Erwartungen bleibt. Deshalb gehören Ergebnis und Prognose zusammen.',
      },
    ],
  },
  market_analyst_sentiment: {
    image: picture('/v3/info/analysts.png', 122.4, 134.3),
    title: 'Was die Profis erwarten – und wie oft sie richtig lagen.',
    intro:
      'Ein einzelnes Rating sagt wenig. Die Analysten-Analyse zeigt dir, wie globale Aktienanalysten eine Aktie einschätzen, wie weit ihre Kursziele auseinanderliegen, mit welchen Argumenten sie ihre Einstufung begründen – und wie treffsicher sie bisher waren.',
    whyTitle: 'Was diese Ansicht besonders macht',
    why: 'Die Daten stammen von TipRanks, einem Spezialisten für die Auswertung von Analysteneinschätzungen. Der entscheidende Unterschied: Du siehst nicht nur, was ein Analyst sagt, sondern auch, wie oft er damit richtig lag und welche Rendite seine Einstufungen im Schnitt gebracht haben. Diese Transparenz war lange vor allem professionellen Investoren vorbehalten. Mit ihr erkennst du, wessen Einschätzung mehr Gewicht verdient.',
    showsTitle: 'Was die Analysten-Analyse zeigt',
    shows: [
      {
        lead: 'Rating-Konsens',
        text: 'Die Verteilung der aktuellen Einstufungen auf Buy, Hold und Sell und der daraus abgeleitete Konsens.',
      },
      {
        lead: 'Durchschnittliches Kursziel',
        text: 'Der Mittelwert aller Kursziele, dazu die Spanne vom niedrigsten bis zum höchsten Ziel und der Abstand zum aktuellen Kurs.',
      },
      {
        lead: 'Top-Analysten',
        text: 'Die Analysten zur Aktie, jeweils mit Trefferquote und durchschnittlicher Rendite ihrer bisherigen Einstufungen.',
      },
      {
        lead: 'Thesen',
        text: 'Die Argumente hinter den Einstufungen – die der Optimisten und die der Skeptiker.',
      },
    ],
    usageTitle: 'So nutzt du die Analysten-Analyse',
    usage: [
      'Auf die Spanne schauen, nicht nur auf den Mittelwert. Liegen die Kursziele weit auseinander, ist die Unsicherheit hoch. Ein Durchschnitt glättet das.',
      'Trefferquoten als Gewicht nutzen. Sie zeigen, wessen Einschätzung sich bisher öfter bestätigt hat. Die Vergangenheit ist aber kein verlässlicher Indikator für die Zukunft.',
      'Die Gegenthese lesen. Hast du bereits eine Meinung, lies gezielt die Argumente der Gegenseite. Sie zeigen dir, was du übersehen könntest.',
      'Auf das Datum achten. Einstufungen vor Quartalszahlen oder wichtigen Meldungen können überholt sein.',
      'Konsens als Erwartung verstehen. Ein klarer Konsens ist oft schon im Kurs enthalten. Spannend wird es, wenn sich Einschätzungen ändern.',
    ],
    closingNote: [
      { text: 'Ratings und Kursziele sind Einschätzungen Dritter. ' },
      { text: 'Sie sind keine Aufforderung, eine Aktie zu kaufen oder zu verkaufen.', bold: true },
    ],
  },
  fundamental_scores: {
    image: picture('/v3/info/scores.png', 166.1, 146.9),
    title: 'Die Substanz eines Unternehmens, messbar in vier Dimensionen.',
    intro:
      'Ist das Geschäftsmodell profitabel? Wächst es? Hält es einer Krise stand? Schüttet es verlässlich aus? Die Scores beantworten diese vier Fragen anhand der Fundamentaldaten – verdichtet zu Werten, die du auf einen Blick lesen und direkt vergleichen kannst.',
    whyTitle: 'Die Methodik dahinter',
    why: 'Die Scores stammen von TraderFox, dem Trading-Spezialisten der Finanzen.net Gruppe, Anbieter von Börsensoftware und Herausgeber von Börsenmagazinen und der Trader-Zeitung. Hinter jedem Score stehen Kennzahlen aus Bilanz, Gewinn- und Verlustrechnung und Cashflow. Was Analysten sonst in eigenen Modellen auswerten, siehst du hier als klares Profil.',
    showsTitle: 'Was die Scores zeigen',
    shows: [
      {
        lead: 'Qualität',
        text: 'Wie profitabel und effizient das Unternehmen wirtschaftet, etwa über Margen und Kapitalrendite.',
      },
      {
        lead: 'Wachstum',
        text: 'Wie stark Umsatz und Gewinn wachsen und wie stabil dieses Wachstum ist.',
      },
      {
        lead: 'Robustheit',
        text: 'Wie widerstandsfähig das Unternehmen ist, etwa gemessen an Verschuldung und Bilanzstärke.',
      },
      {
        lead: 'Dividende',
        text: 'Wie hoch, verlässlich und nachhaltig die Ausschüttung ist.',
      },
    ],
    conclusion:
      'Jeder Score setzt sich aus mehreren Unterscores zusammen. Sie zeigen, woher eine Bewertung kommt. Dazu kommen Kennzahlen wie die Rule of 40.',
    usageTitle: 'So nutzt du die Scores',
    usage: [
      'Das Profil lesen, nicht nur die Höhe. Welche Scores zählen, hängt von deinem Ziel ab. Ein Wachstumswert mit niedrigem Dividenden-Score ist kein Widerspruch.',
      'In die Unterscores gehen. Ein mittlerer Score kann aus einem sehr hohen und einem sehr niedrigen Wert bestehen. Erst die Unterscores zeigen, welcher Fall vorliegt.',
      'Innerhalb der Branche vergleichen. Ein Versorger wächst anders als ein Softwareunternehmen. Die Wettbewerbsanalyse zeigt dir die Scores der direkten Konkurrenz.',
      'Mit den Earnings abgleichen. Scores beruhen auf veröffentlichten Zahlen. Aktuelle Quartalszahlen zeigen, ob sich das Bild gerade verschiebt.',
    ],
    closingNote: [
      { text: 'Wichtig:', bold: true },
      {
        text: ' Die Scores bewerten das Unternehmen, nicht den Preis der Aktie. Ob der aktuelle Kurs die Qualität bereits abbildet, beantworten sie nicht.',
      },
    ],
  },
  competitive: {
    image: picture('/v3/info/competitors.png', 140.8, 134.7),
    title: 'Wie sich eine Aktie gegen ihre Konkurrenz behauptet.',
    intro:
      'Ob ein Unternehmen stark ist, zeigt sich erst im Vergleich. Die Wettbewerbsanalyse stellt Stärken und Risiken gegenüber und setzt die Aktie neben ihre wichtigsten Wettbewerber – nach Earnings, Analysteneinschätzungen und Scores.',
    whyTitle: 'Research-Standard, jetzt für dich',
    why: 'Im institutionellen Research gehört der Vergleich mit der Konkurrenz zum Standard. Für Privatanleger war er bisher Handarbeit: Kennzahlen aus Geschäftsberichten, Kursziele aus Analystenstudien, Bewertungen aus verschiedenen Quellen. Die Wettbewerbsanalyse führt die Daten von FactSet, TipRanks und TraderFox in einer Tabelle zusammen. So siehst du in Sekunden, ob eine Stärke das Unternehmen auszeichnet – oder die ganze Branche.',
    showsTitle: 'Was die Wettbewerbsanalyse zeigt',
    shows: [
      {
        lead: 'Stärken',
        text: 'Die zentralen Wettbewerbsvorteile des Unternehmens, jeweils kurz begründet – etwa Marktstellung, Technologie oder Kostenstruktur.',
      },
      {
        lead: 'Risiken',
        text: 'Die wichtigsten Faktoren, die das Geschäft gefährden können – von neuer Konkurrenz bis zu Abhängigkeiten von einzelnen Kunden oder Märkten.',
      },
      {
        lead: 'Vergleich der Top-Wettbewerber',
        text: 'Eine Tabelle mit den direkten Konkurrenten, verglichen nach Earnings, Analysten und Scores.',
      },
    ],
    usageTitle: 'So nutzt du die Wettbewerbsanalyse',
    usage: [
      'Risiken einordnen. Prüfe, ob ein Risiko die ganze Branche betrifft oder nur dieses Unternehmen. Das eine ist ein Marktthema, das andere ein Unternehmensthema.',
      'Relativ statt absolut lesen. Schwache Earnings wirken anders, wenn die gesamte Konkurrenz ebenfalls schwächelt. Stark ist, wer sich vom Feld abhebt.',
      'Die Tabelle entlang deiner Frage lesen. Geht es dir um Profitabilität, schau auf die Scores. Geht es um die Erwartungen des Markts, auf die Analysten.',
      'Alternativen kennenlernen. Im Vergleich tauchen oft Unternehmen auf, die du noch nicht auf dem Schirm hattest. Ihre AI Analyse erreichst du direkt über die Aktienseite.',
    ],
  },
};
